import React, { useState, useEffect, useRef } from "react";
import { Select, Input } from "antd";
import {
  getVisitMedicines,
  addVisitMedicine,
  updateVisitMedicine,
  stopVisitMedicine,
} from "../../../../apis/visit";
import { getMedicines } from "../../../../apis/medicine";

interface DoctorRoundsFormProps {
  initialData?: any;
  visit: { id?: number; visitId?: number } | any;
  onSave: (data: any) => void;
  onCancel: () => void;
}

const formatDoseRoute = (instructions: any): string => {
  if (!instructions) return "—";
  const dose = instructions.dose ?? instructions.power
    ? `${instructions.power ?? ""}${instructions.powerUnit ?? instructions.power_unit ?? ""}`.trim() || instructions.dose
    : "";
  const route = instructions.route ?? "";
  return [dose, route].filter(Boolean).join(" / ") || "—";
};

const formatFrequency = (instructions: any): string => {
  if (!instructions) return "—";
  if (instructions.frequency) return instructions.frequency;
  const parts: string[] = [];
  if (instructions.morning) parts.push("Morning");
  if (instructions.afternoon) parts.push("Afternoon");
  if (instructions.evening) parts.push("Evening");
  if (instructions.night) parts.push("Night");
  if (parts.length > 0) return parts.join(" + ");
  if (instructions.type === "sos") return "SOS";
  return "—";
};

const DoctorRoundsForm: React.FC<DoctorRoundsFormProps> = ({
  initialData,
  visit,
  onSave,
  onCancel,
}) => {
  const visitId = visit?.id ?? visit?.visitId;

  const [plan, setPlan] = useState(initialData?.plan ?? "");

  const [medicines, setMedicines] = useState<any[]>([]);
  const [loadingMeds, setLoadingMeds] = useState(false);
  const [medicationsUpdated, setMedicationsUpdated] = useState(false);

  // inline modify state: medicineId -> boolean
  const [modifyingId, setModifyingId] = useState<number | null>(null);
  const [modifyData, setModifyData] = useState<any>({});

  // add medicine state
  const [medicineOptions, setMedicineOptions] = useState<any[]>([]);
  const [medicineSearch, setMedicineSearch] = useState("");
  const [selectedMedicineId, setSelectedMedicineId] = useState<number | null>(null);
  const [addDose, setAddDose] = useState("");
  const [addRoute, setAddRoute] = useState("");
  const [addFrequency, setAddFrequency] = useState("");
  const [addNotes, setAddNotes] = useState("");
  const [addingMed, setAddingMed] = useState(false);

  const [frequencyOptions, setFrequencyOptions] = useState([
    "OD", "BD", "TDS", "QID", "SOS", "HS", "Morning", "Evening", "Night",
    "Morning + Evening", "Morning + Night", "Morning + Evening + Night",
  ]);
  const [newFrequency, setNewFrequency] = useState("");
  const frequencyInputRef = useRef<any>(null);

  const [notesOptions, setNotesOptions] = useState([
    "Before food", "After food", "With food", "Empty stomach",
    "With water", "With milk", "Crush and mix", "Do not crush",
  ]);
  const [newNote, setNewNote] = useState("");
  const notesInputRef = useRef<any>(null);

  const addFrequencyOption = () => {
    const val = newFrequency.trim();
    if (val && !frequencyOptions.includes(val)) {
      setFrequencyOptions((prev) => [...prev, val]);
    }
    if (val) setAddFrequency(val);
    setNewFrequency("");
  };

  const addNoteOption = () => {
    const val = newNote.trim();
    if (val && !notesOptions.includes(val)) {
      setNotesOptions((prev) => [...prev, val]);
    }
    if (val) setAddNotes(val);
    setNewNote("");
  };

  const fetchMedicines = () => {
    if (!visitId) return;
    setLoadingMeds(true);
    getVisitMedicines(visitId)
      .then((res: any) => {
        const data: any[] = res?.data?.data ?? [];
        setMedicines(data.filter((m: any) => m.status === "active"));
      })
      .catch(() => {})
      .finally(() => setLoadingMeds(false));
  };

  useEffect(() => {
    fetchMedicines();
  }, [visitId]);

  const searchMedicines = (search: string) => {
    setMedicineSearch(search);
    getMedicines({ name: search, per_page: 50, autocomplete: true })
      .then((res: any) => {
        const data: any[] = res?.data?.data ?? [];
        setMedicineOptions(data.map((m: any) => ({ value: m.id, label: m.name })));
      })
      .catch(() => {});
  };

  const handleStop = async (med: any) => {
    if (!visitId) return;
    try {
      await stopVisitMedicine(visitId, med.id);
      setMedicationsUpdated(true);
      fetchMedicines();
    } catch {}
  };

  const handleModifyOpen = (med: any) => {
    setModifyingId(med.id);
    setModifyData({
      dose: med.instructions?.dose ?? med.instructions?.power ?? "",
      route: med.instructions?.route ?? "",
      frequency: med.instructions?.frequency ?? "",
      notes: med.instructions?.notes ?? "",
    });
  };

  const handleModifySave = async (med: any) => {
    if (!visitId) return;
    try {
      await updateVisitMedicine(visitId, med.id, {
        instructions: {
          ...med.instructions,
          dose: modifyData.dose,
          route: modifyData.route,
          frequency: modifyData.frequency,
          notes: modifyData.notes,
        },
      });
      setMedicationsUpdated(true);
      setModifyingId(null);
      fetchMedicines();
    } catch {}
  };

  const handleAddMedicine = async () => {
    if (!visitId || !selectedMedicineId) return;
    setAddingMed(true);
    try {
      await addVisitMedicine(visitId, {
        medicineId: selectedMedicineId,
        instructions: {
          dose: addDose,
          route: addRoute,
          frequency: addFrequency,
          notes: addNotes,
        },
        status: "active",
      });
      setMedicationsUpdated(true);
      setSelectedMedicineId(null);
      setAddDose("");
      setAddRoute("");
      setAddFrequency("");
      setAddNotes("");
      setMedicineSearch("");
      setMedicineOptions([]);
      fetchMedicines();
    } catch {}
    setAddingMed(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      plan,
      medicationsUpdated,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-6">
      {/* PLAN */}
      <div>
        <h3 className="text-xs font-semibold tracking-widest text-gray-500 uppercase mb-2">Plan</h3>
        <textarea
          value={plan}
          onChange={(e) => setPlan(e.target.value)}
          rows={3}
          placeholder="Enter treatment plan..."
          className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-200"
        />
      </div>

      {/* MEDICATIONS */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold tracking-widest text-gray-500 uppercase">Medications</h3>
          <span className="text-xs text-gray-400">Current prescription — modify, stop, or add below</span>
        </div>

        {loadingMeds ? (
          <p className="text-sm text-gray-400 py-2">Loading medicines...</p>
        ) : medicines.length === 0 ? (
          <p className="text-sm text-gray-400 py-2">No active medications.</p>
        ) : (
          <div className="rounded border border-gray-200 overflow-hidden mb-3">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase w-2/5">Medicine</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Dose / Route</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Frequency</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {medicines.map((med: any) => (
                  <React.Fragment key={med.id}>
                    <tr className="bg-white hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <span className="font-medium text-gray-900">{med.medicineName}</span>
                        {med.instructions?.notes && (
                          <div className="text-xs text-gray-400 mt-0.5">{med.instructions.notes}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-700">{formatDoseRoute(med.instructions)}</td>
                      <td className="px-3 py-2 text-gray-700">{formatFrequency(med.instructions)}</td>
                      <td className="px-3 py-2">
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => modifyingId === med.id ? setModifyingId(null) : handleModifyOpen(med)}
                            className="text-xs px-2 py-1 rounded border border-indigo-300 text-indigo-600 hover:bg-indigo-50"
                          >
                            {modifyingId === med.id ? "Cancel" : "Modify"}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleStop(med)}
                            className="text-xs px-2 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50"
                          >
                            Stop
                          </button>
                        </div>
                      </td>
                    </tr>
                    {modifyingId === med.id && (
                      <tr className="bg-indigo-50">
                        <td colSpan={4} className="px-3 py-2">
                          <div className="grid grid-cols-4 gap-2">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Dose</label>
                              <input
                                type="text"
                                value={modifyData.dose}
                                onChange={(e) => setModifyData({ ...modifyData, dose: e.target.value })}
                                className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:border-indigo-400"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Route</label>
                              <input
                                type="text"
                                value={modifyData.route}
                                onChange={(e) => setModifyData({ ...modifyData, route: e.target.value })}
                                className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:border-indigo-400"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Frequency</label>
                              <input
                                type="text"
                                value={modifyData.frequency}
                                onChange={(e) => setModifyData({ ...modifyData, frequency: e.target.value })}
                                className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:border-indigo-400"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Notes</label>
                              <input
                                type="text"
                                value={modifyData.notes}
                                onChange={(e) => setModifyData({ ...modifyData, notes: e.target.value })}
                                className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:border-indigo-400"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2 mt-2">
                            <button
                              type="button"
                              onClick={() => handleModifySave(med)}
                              className="text-xs px-3 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => setModifyingId(null)}
                              className="text-xs px-3 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Add medicine */}
        <div className="rounded border border-dashed border-gray-300 p-3 bg-gray-50">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Add Medicine</p>
          <div className="grid grid-cols-5 gap-2">
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Medicine</label>
              <Select
                showSearch
                value={selectedMedicineId}
                placeholder="Search medicine..."
                filterOption={false}
                onSearch={searchMedicines}
                onFocus={() => { if (medicineOptions.length === 0) searchMedicines(""); }}
                onChange={(val) => setSelectedMedicineId(val)}
                options={medicineOptions}
                className="w-full"
                style={{ height: 32 }}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Dose</label>
              <input
                type="text"
                value={addDose}
                onChange={(e) => setAddDose(e.target.value)}
                className="w-full rounded border border-gray-300 px-2 text-sm focus:outline-none focus:border-indigo-400"
                style={{ height: 32 }}
                placeholder="e.g. 500mg"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Route</label>
              <Select
                value={addRoute || undefined}
                placeholder="Select"
                onChange={(val) => setAddRoute(val)}
                className="w-full"
                style={{ height: 32 }}
                options={[
                  { value: "Oral", label: "Oral" },
                  { value: "IV", label: "IV" },
                  { value: "IV drip", label: "IV drip" },
                  { value: "IM", label: "IM" },
                  { value: "SC", label: "SC" },
                  { value: "Topical", label: "Topical" },
                  { value: "Inhaled", label: "Inhaled" },
                ]}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Frequency</label>
              <Select
                value={addFrequency || undefined}
                placeholder="Select or add..."
                onChange={(val) => setAddFrequency(val)}
                className="w-full"
                style={{ height: 32 }}
                options={frequencyOptions.map((f) => ({ value: f, label: f }))}
                dropdownStyle={{ minWidth: 300 }}
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <div className="border-t border-gray-100 mt-1 px-3 py-2.5 bg-gradient-to-b from-white to-gray-50">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
                        Create custom
                      </p>
                      <div className="flex items-center gap-2">
                        <Input
                          ref={frequencyInputRef}
                          placeholder="e.g. Every 6 hours"
                          value={newFrequency}
                          onChange={(e) => setNewFrequency(e.target.value)}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === "Enter") addFrequencyOption();
                          }}
                          style={{ fontSize: 12, borderRadius: 6 }}
                        />
                        <button
                          type="button"
                          onClick={addFrequencyOption}
                          disabled={!newFrequency.trim()}
                          className="shrink-0 px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          + Add
                        </button>
                      </div>
                    </div>
                  </>
                )}
              />
            </div>
          </div>
          <div className="mt-2 flex gap-2 items-end">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Notes</label>
              <Select
                value={addNotes || undefined}
                placeholder="Select or add..."
                onChange={(val) => setAddNotes(val)}
                className="w-full"
                style={{ height: 32 }}
                options={notesOptions.map((n) => ({ value: n, label: n }))}
                dropdownStyle={{ minWidth: 360 }}
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <div className="border-t border-gray-100 mt-1 px-3 py-2.5 bg-gradient-to-b from-white to-gray-50">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
                        Create custom
                      </p>
                      <div className="flex items-center gap-2">
                        <Input
                          ref={notesInputRef}
                          placeholder="e.g. Dilute before IV"
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === "Enter") addNoteOption();
                          }}
                          style={{ fontSize: 12, borderRadius: 6 }}
                        />
                        <button
                          type="button"
                          onClick={addNoteOption}
                          disabled={!newNote.trim()}
                          className="shrink-0 px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          + Add
                        </button>
                      </div>
                    </div>
                  </>
                )}
              />
            </div>
            <button
              type="button"
              onClick={handleAddMedicine}
              disabled={!selectedMedicineId || addingMed}
              className="px-4 text-sm font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ height: 32 }}
            >
              {addingMed ? "Adding..." : "Add"}
            </button>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
        >
          Save Doctor Rounds
        </button>
      </div>
    </form>
  );
};

export default DoctorRoundsForm;
